import { z } from "zod";
import { SharedRepoSchema } from "./shared-bots.js";

const sha = z.string().regex(/^[a-f0-9]{40,64}$/);
export const MissionRepositorySnapshotSchema = z.object({
  kind: z.literal("repository").default("repository"),
  repository: SharedRepoSchema,
  /** Null denotes a pinned commit observation, never pull-request or release evidence. */
  number: z.number().int().positive().nullable(),
  url: z.string(),
  title: z.string(),
  baseSha: sha,
  baseBranch: z.string().nullable().optional(),
  pullState: z.string().nullable().optional(),
  merged: z.boolean().nullable().optional(),
  headSha: sha,
  branch: z.string(),
  capturedAt: z.iso.datetime(),
  producingRunId: z.string().nullable(),
  sourceKey: z.string(),
  files: z.array(
    z.object({
      path: z.string(),
      previousPath: z.string().nullable(),
      status: z.string(),
      additions: z.number(),
      deletions: z.number(),
      patch: z.string().nullable(),
      patchUnavailable: z.boolean(),
      source: z.string().max(16000).nullable().optional(),
      sourceTruncated: z.boolean().optional(),
    }),
  ),
  filesComplete: z.boolean(),
  checks: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      status: z.string(),
      conclusion: z.string().nullable(),
      url: z.string().nullable(),
      startedAt: z.string().nullable(),
      completedAt: z.string().nullable(),
      summary: z.string(),
      text: z.string(),
      actionsJobId: z.number().nullable(),
      outputTruncated: z.boolean().default(false),
    }),
  ),
  checksComplete: z.boolean(),
});
export type MissionRepositorySnapshot = z.infer<typeof MissionRepositorySnapshotSchema>;
export const MissionRepositoryInput = z
  .object({ id: z.string().min(1), assignmentKey: z.string().min(1) })
  .strict();
export const MissionRepositoryLineRangeSchema = z
  .object({
    start: z.number().int().positive().describe("First source line, inclusive and 1-based."),
    end: z.number().int().positive().describe("Last source line, inclusive; up to 2000 lines."),
  })
  .strict()
  .refine((range) => range.end >= range.start && range.end - range.start < 2000);
export type MissionRepositoryLineRange = z.infer<typeof MissionRepositoryLineRangeSchema>;
/** Planning reads do not require an assignment, PR, or acceptance evidence. */
export const MissionRepositoryInspectInput = z
  .object({
    id: z.string().min(1),
    repository: SharedRepoSchema,
    ref: z
      .string()
      .min(1)
      .max(255)
      .regex(/^[^\s?#\p{Cc}]+$/u),
    path: z.string().min(1).max(1000).optional(),
    lines: MissionRepositoryLineRangeSchema.optional(),
  })
  .strict()
  .refine((input) => !input.lines || !!input.path, "Choose a file for a line range.");
export const MissionRepositoryFileInput = MissionRepositoryInput.extend({
  evidenceId: z.string().min(1),
  path: z.string().min(1).max(500),
  lines: MissionRepositoryLineRangeSchema.optional(),
}).strict();
export const MissionRepositoryFileSchema = z.object({
  path: z.string(),
  headSha: sha,
  content: z.string(),
  truncated: z.boolean(),
  lines: z
    .object({
      start: z.number().int().positive(),
      end: z.number().int().positive(),
      total: z.number().int().positive(),
    })
    .optional(),
  excerpt: z.boolean().optional(),
});

export const MissionCheckLogInput = MissionRepositoryInput.extend({
  evidenceId: z.string().min(1),
  checkId: z.number().int().positive(),
  search: z
    .string()
    .min(1)
    .max(200)
    .optional()
    .describe(
      "Optional literal text to locate in the provider log; returns and retains surrounding lines as evidence.",
    ),
}).strict();
export const MissionCheckLogSchema = z.object({
  name: z.string(),
  headSha: sha,
  content: z.string(),
});

/** Source context read from the recorded repository commit, including unchanged files. */
export const MissionRepositorySourceSchema = MissionRepositoryFileSchema.extend({
  kind: z.literal("repository_file"),
  repository: SharedRepoSchema,
  sourceEvidenceId: z.string().min(1),
  content: z.string().max(250000),
  capturedAt: z.iso.datetime(),
});

export const MissionRepositorySourceSummarySchema = MissionRepositorySourceSchema.omit({
  content: true,
}).extend({ contentCharacters: z.number().int().nonnegative() });

export const MissionRepositorySummarySchema = MissionRepositorySnapshotSchema.omit({
  files: true,
  checks: true,
}).extend({ fileCount: z.number(), checkCount: z.number() });
export const MissionRepositorySnapshotInput = MissionRepositoryInput.extend({
  evidenceId: z.string().min(1),
}).strict();

export const MissionCheckLogEvidenceSchema = z.object({
  kind: z.literal("check_log"),
  repository: SharedRepoSchema,
  sourceEvidenceId: z.string(),
  checkId: z.number().int().positive(),
  jobId: z.number().int().positive(),
  name: z.string(),
  headSha: sha,
  content: z.string().max(20000),
  startLine: z.number().int().nonnegative(),
  endLine: z.number().int().nonnegative(),
  totalLines: z.number().int().nonnegative(),
  truncated: z.boolean(),
  capturedAt: z.iso.datetime(),
});
